import { createContext, useRef, useContext, useState, useEffect } from 'react';
import eq from 'react-fast-compare';
import { jsx } from 'react/jsx-runtime';

const ClientContext = /*#__PURE__*/createContext(void 0);

function useOpts(arg) {
  const argRef = useRef(arg);
  if (!eq(arg, argRef.current)) {
    argRef.current = arg;
  }
  return argRef.current;
}

const EMPTY_CONFIG = {};
const isSSR = typeof window === "undefined";
async function getNewClient(config) {
  if (config === EMPTY_CONFIG || isSSR) {
    return void 0;
  }
  const StreamrClient = (await import('@streamr/sdk')).default;
  return new StreamrClient(config);
}
function useClient(config = EMPTY_CONFIG, cacheKey) {
  const parentClient = useContext(ClientContext);
  const [client, setClient] = useState();
  const conf = useOpts(config);
  useEffect(() => {
    let mounted = true;
    if (conf === EMPTY_CONFIG) {
      return void setClient(void 0);
    }
    void (async () => {
      const newClient = await getNewClient(conf);
      if (mounted) {
        return void setClient(newClient);
      }
      newClient?.destroy();
    })();
    return () => {
      mounted = false;
    };
  }, [conf, cacheKey]);
  useEffect(() => () => void client?.destroy(), [client]);
  return config === EMPTY_CONFIG ? parentClient : client;
}

function resend(stream, options, streamrClient, {
  ignoreUndecodedMessages = false,
  onMessageError,
  onError
} = {}) {
  let cancelled = false;
  const rs = new ReadableStream({
    async start(controller) {
      try {
        if (streamrClient.destroySignal.isDestroyed()) {
          return;
        }
        const queue = await streamrClient.resend(stream, options);
        if (cancelled) {
          return;
        }
        queue.pipeline.onMessage.listen(streamMessage => {
          controller.enqueue(streamMessage);
        });
        queue.onError.listen(e => {
          if (typeof onMessageError === "function") {
            onMessageError(e);
          }
          const msg = e.streamMessage;
          if (!msg) {
            return;
          }
          if (!ignoreUndecodedMessages) {
            controller.enqueue(msg);
          }
        });
        for await (const _ of queue) {}
      } catch (e) {
        onError?.(e);
      } finally {
        controller.close();
      }
    },
    cancel() {
      cancelled = true;
    }
  });
  const reader = rs.getReader();
  return {
    async next() {
      return reader.read();
    },
    async abort() {
      await reader.cancel();
    }
  };
}

function useResend(streamDef, resendOptions = {
  last: 1
}, {
  cacheKey,
  disabled = false,
  ignoreUndecodedMessages = false,
  onAfterFinish,
  onBeforeStart,
  onError,
  onMessage,
  onMessageError
} = {}) {
  const stream = useOpts(streamDef);
  const opts = useOpts(resendOptions);
  const client = useClient();
  const onMessageRef = useRef(onMessage);
  if (onMessageRef.current !== onMessage) {
    onMessageRef.current = onMessage;
  }
  const onErrorRef = useRef(onError);
  if (onErrorRef.current !== onError) {
    onErrorRef.current = onError;
  }
  const onMessageErrorRef = useRef(onMessageError);
  if (onMessageErrorRef.current !== onMessageError) {
    onMessageErrorRef.current = onMessageError;
  }
  const onBeforeStartRef = useRef(onBeforeStart);
  if (onBeforeStartRef.current !== onBeforeStart) {
    onBeforeStartRef.current = onBeforeStart;
  }
  const onAfterFinishRef = useRef(onAfterFinish);
  if (onAfterFinishRef.current !== onAfterFinish) {
    onAfterFinishRef.current = onAfterFinish;
  }
  useEffect(() => {
    if (disabled || !client) {
      return () => {};
    }
    onBeforeStartRef.current?.();
    const queue = resend(stream, opts, client, {
      onError(e) {
        onErrorRef.current?.(e);
      },
      onMessageError(e) {
        onMessageErrorRef.current?.(e);
      },
      ignoreUndecodedMessages
    });
    void (async q => {
      while (true) {
        const {
          value,
          done
        } = await q.next();
        if (value) {
          onMessageRef.current?.(value);
        }
        if (done) {
          break;
        }
      }
      onAfterFinishRef.current?.();
    })(queue);
    return () => {
      queue?.abort();
    };
  }, [stream, opts, client, disabled, ignoreUndecodedMessages, cacheKey]);
}

function subscribe(stream, streamrClient, {
  ignoreUndecodedMessages = false,
  onError,
  onMessageError,
  resendOptions
} = {}) {
  let sub;
  let cancelled = false;
  function unsub() {
    if (sub) {
      streamrClient.unsubscribe(sub);
      sub = void 0;
    }
  }
  const rs = new ReadableStream({
    async start(controller) {
      try {
        if (streamrClient.destroySignal.isDestroyed()) {
          return;
        }
        const options = typeof stream === "string" ? {
          id: stream
        } : stream;
        sub = await streamrClient.subscribe({
          ...options,
          resend: resendOptions
        },
        // Does not fly without the "legacy" on-message callback.
        () => {});
        if (cancelled) {
          return void unsub();
        }
        sub.pipeline.onMessage.listen(streamMessage => {
          controller.enqueue(streamMessage);
        });
        sub.on("error", e => {
          onMessageError?.(e);
          const raw = e.streamMessage;
          if (!raw) {
            return;
          }
          if (!ignoreUndecodedMessages) {
            controller.enqueue(raw);
          }
        });
      } catch (e) {
        onError?.(e);
        controller.close();
      }
    },
    cancel() {
      unsub();
      cancelled = true;
    }
  });
  const reader = rs.getReader();
  return {
    async next() {
      return reader.read();
    },
    async abort() {
      await reader.cancel();
    }
  };
}

function useSubscribe(streamDef, {
  cacheKey,
  disabled = false,
  ignoreUndecodedMessages = false,
  onAfterFinish,
  onBeforeStart,
  onError,
  onMessage,
  onMessageError,
  resendOptions: resendOptionsProp
} = {}) {
  const stream = useOpts(streamDef);
  const resendOptions = useOpts(resendOptionsProp);
  const client = useClient();
  const onMessageRef = useRef(onMessage);
  if (onMessageRef.current !== onMessage) {
    onMessageRef.current = onMessage;
  }
  const onErrorRef = useRef(onError);
  if (onErrorRef.current !== onError) {
    onErrorRef.current = onError;
  }
  const onMessageErrorRef = useRef(onMessageError);
  if (onMessageErrorRef.current !== onMessageError) {
    onMessageErrorRef.current = onMessageError;
  }
  const onBeforeStartRef = useRef(onBeforeStart);
  if (onBeforeStartRef.current !== onBeforeStart) {
    onBeforeStartRef.current = onBeforeStart;
  }
  const onAfterFinishRef = useRef(onAfterFinish);
  if (onAfterFinishRef.current !== onAfterFinish) {
    onAfterFinishRef.current = onAfterFinish;
  }
  useEffect(() => {
    if (disabled || !client) {
      return () => {};
    }
    onBeforeStartRef.current?.();
    const queue = subscribe(stream, client, {
      onError(e) {
        onErrorRef.current?.(e);
      },
      onMessageError(e) {
        onMessageErrorRef.current?.(e);
      },
      ignoreUndecodedMessages,
      resendOptions
    });
    void (async q => {
      while (true) {
        const {
          value,
          done
        } = await q.next();
        if (value) {
          onMessageRef.current?.(value);
        }
        if (done) {
          break;
        }
      }
      onAfterFinishRef.current?.();
    })(queue);
    return () => {
      queue?.abort();
    };
  }, [disabled, stream, client, ignoreUndecodedMessages, resendOptions, cacheKey]);
}

function Provider({
  children,
  cacheKey,
  ...props
}) {
  const client = useClient(props, cacheKey);
  return /*#__PURE__*/jsx(ClientContext.Provider, {
    value: client,
    children: children
  });
}

export { ClientContext, Provider as default, resend, subscribe, useClient, useResend, useSubscribe };
